import React, { createContext, useState, useEffect, ReactNode } from "react";
import { fetchAuthSession, signOut } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";

// Define the Tokens interface (assuming it's missing)
export interface Tokens {
  accessToken?: string;
  idToken?: string;
  refreshToken?: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  tokens: Tokens | null;
  isDevBypass: boolean;
  login: (tokens: Tokens) => void;
  logout: () => void;
  toggleDevBypass: () => void;
  refreshSession: () => Promise<void>;
}

const defaultAuthContext: AuthContextType = {
  isAuthenticated: false,
  isLoading: true,
  tokens: null,
  isDevBypass: false,
  login: () => {},
  logout: () => {},
  toggleDevBypass: () => {},
  refreshSession: async () => {},
};

export const AuthContext = createContext<AuthContextType>(defaultAuthContext);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [tokens, setTokens] = useState<Tokens | null>(null);
  const [isDevBypass, setIsDevBypass] = useState(false);

  // Check if we're in dev mode or on allowed domains
  const isDevelopment = import.meta.env.DEV;
  const currentHostname = window.location.hostname;
  const allowedDevDomains = [
    "dev.gesslargardens.com",
    "stage.gesslargardens.com",
  ];
  const isAllowedDomain = allowedDevDomains.includes(currentHostname);
  const isCloudFrontDomain = currentHostname.endsWith("cloudfront.net");

  // Allow dev bypass in development mode OR on specified domains OR on CloudFront domains OR if VITE_DEV_AUTH_BYPASS is explicitly set
  const canUseDevBypass =
    isDevelopment ||
    isAllowedDomain ||
    isCloudFrontDomain ||
    import.meta.env.VITE_DEV_AUTH_BYPASS === "true";

  const shouldAutoEnableDevBypass =
    canUseDevBypass && import.meta.env.VITE_DEV_AUTH_BYPASS === "true";

  useEffect(() => {
    (async () => {
      try {
        const session = await fetchAuthSession();
        const at = (session as any)?.tokens?.accessToken?.toString?.();
        const idt = (session as any)?.tokens?.idToken?.toString?.();
        if (at || idt) {
          setTokens({ accessToken: at, idToken: idt });
          setIsAuthenticated(true);
        }
      } catch {
        // unauthenticated is fine
      }

      // Dev bypass handling: respect explicit user choice in localStorage if present
      if (canUseDevBypass) {
        const stored = localStorage.getItem("gesslar_dev_bypass");
        if (stored === "true" || stored === "false") {
          setIsDevBypass(stored === "true");
        } else if (shouldAutoEnableDevBypass) {
          setIsDevBypass(true);
          localStorage.setItem("gesslar_dev_bypass", "true");
        }
      }

      setIsLoading(false);
    })();
  }, [canUseDevBypass, shouldAutoEnableDevBypass]);

  const refreshSession = async (): Promise<void> => {
    try {
      const session = await fetchAuthSession();
      const at = (session as any)?.tokens?.accessToken?.toString?.();
      const idt = (session as any)?.tokens?.idToken?.toString?.();
      setTokens(at || idt ? { accessToken: at, idToken: idt } : null);
      setIsAuthenticated(Boolean(at || idt));
    } catch {
      setTokens(null);
      setIsAuthenticated(false);
    }
  };

  // Update auth state reactively when Amplify auth events occur
  useEffect(() => {
    const remove = Hub.listen("auth", async ({ payload }) => {
      switch (payload.event) {
        case "signedIn":
        case "tokenRefresh": {
          try {
            const session = await fetchAuthSession();
            const at = (session as any)?.tokens?.accessToken?.toString?.();
            const idt = (session as any)?.tokens?.idToken?.toString?.();
            setTokens(at || idt ? { accessToken: at, idToken: idt } : null);
            setIsAuthenticated(Boolean(at || idt));
          } catch {
            setTokens(null);
            setIsAuthenticated(false);
          }
          break;
        }
        case "signedOut": {
          setTokens(null);
          setIsAuthenticated(false);
          break;
        }
        default:
          break;
      }
    });
    return () => remove();
  }, []);

  const login = (_newTokens: Tokens) => {
    // Hosted UI handles login; nothing to do here
  };

  const logout = async () => {
    try {
      await signOut({ global: true });
    } catch {}
    setTokens(null);
    setIsAuthenticated(false);
    // Preserve user's dev bypass preference across logout
  };

  const toggleDevBypass = () => {
    if (!canUseDevBypass) return; // Only allow in development or allowed domains

    const newBypassState = !isDevBypass;
    setIsDevBypass(newBypassState);
    localStorage.setItem("gesslar_dev_bypass", newBypassState.toString());
  };

  // In development mode or allowed domains, treat isAuthenticated as true if dev bypass is enabled
  const effectiveIsAuthenticated =
    isAuthenticated || (canUseDevBypass && isDevBypass);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: effectiveIsAuthenticated,
        isLoading,
        tokens,
        isDevBypass,
        login,
        logout,
        toggleDevBypass,
        refreshSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
